import _ from 'lodash';

/**
 * Utilities for matching `$identifier` variable tokens in Seeq Formula text.
 *
 * The Formula language binds variables case-insensitively, so `$Temperature` may legally pair
 * with a parameter named `temperature`. Identifiers are matched as whole tokens — longest first,
 * with a trailing guard — so `$a` never matches inside `$abc`, legacy hyphenated identifiers
 * (e.g. asset group column names like `some-thing`) match as one token, and `$a-$b` still reads
 * as subtraction when no hyphenated identifier matches. Matching is text-based: tokens inside
 * string literals and comments are treated like any other occurrence.
 */

const buildVariableTokenPattern = (identifiers: string[]): RegExp | undefined => {
  const known = identifiers.filter((identifier) => identifier !== '');
  if (!known.length) return undefined;
  const alternation = known
    .sort((a, b) => b.length - a.length)
    .map((identifier) => _.escapeRegExp(identifier))
    .join('|');
  return new RegExp(`\\$(${alternation})(?![a-zA-Z0-9_])`, 'gi');
};

/** Replaces whole `$identifier` tokens in a single pass, so a new identifier that collides with
 * another old one can't be rewritten twice. Map keys are matched case-insensitively. */
export function renameFormulaVariables(formula: string, newIdentifierByOld: ReadonlyMap<string, string>): string {
  const identifierByLowercaseOld = new Map(
    [...newIdentifierByOld].map(([oldId, newId]) => [oldId.toLowerCase(), newId]),
  );
  const pattern = buildVariableTokenPattern([...identifierByLowercaseOld.keys()]);
  if (!pattern) return formula;
  return formula.replace(pattern, (_token, oldId: string) => `$${identifierByLowercaseOld.get(oldId.toLowerCase())}`);
}

/** Whether the formula references `$identifier` as a whole token (case-insensitive). */
export function formulaReferencesVariable(formula: string, identifier: string): boolean {
  const pattern = buildVariableTokenPattern([identifier]);
  return pattern ? pattern.test(formula) : false;
}
